import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { clientService } from '../../services/clientService';
import { useAuth } from '../../contexts/AuthContext';
import ClientActivityTimeline from './components/ClientActivityTimeline';
import ClientPhotoGallery from './components/ClientPhotoGallery';
import ClientDocuments from './components/ClientDocuments';
import ClientComplianceReport from './components/ClientComplianceReport';
import NotesFeed from '../../components/ui/NotesFeed';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import { supabase } from '../../lib/supabase';

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  planning: 'bg-blue-100 text-blue-700',
  on_hold: 'bg-amber-100 text-amber-700',
  completed: 'bg-slate-100 text-slate-700',
  archived: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  active: 'Active',
  planning: 'Planning',
  on_hold: 'On Hold',
  completed: 'Completed',
  archived: 'Archived',
};

const COMPLIANCE_COLORS = {
  compliant: 'text-emerald-600',
  warning: 'text-amber-600',
  critical: 'text-red-600',
};

// All possible tabs — each has a visibilityKey that maps to siteVisibility
const ALL_TABS = [
  { id: 'overview', label: 'Overview', icon: 'LayoutDashboard', visibilityKey: 'canViewOverview' },
  { id: 'timeline', label: 'Activity Timeline', icon: 'Activity', visibilityKey: 'canViewActivityTimeline' },
  { id: 'notes', label: 'Notes', icon: 'StickyNote', visibilityKey: 'canViewNotes' },
  { id: 'photos', label: 'Site Photos', icon: 'Images', visibilityKey: 'canViewPhotos' },
  { id: 'documents', label: 'Documents', icon: 'FileText', visibilityKey: 'canViewDocuments' },
  { id: 'drawings', label: 'Drawings', icon: 'FileImage', visibilityKey: 'canViewDrawings' },
  { id: 'snags', label: 'Snags', icon: 'ClipboardList', visibilityKey: 'canViewSnags' },
  { id: 'reports', label: 'Reports', icon: 'BarChart2', visibilityKey: 'canViewReports' },
  { id: 'site-diary', label: 'Site Diary', icon: 'CalendarCheck', visibilityKey: 'canViewSiteDiary' },
  { id: 'rams', label: 'H&S Docs', icon: 'ShieldCheck', visibilityKey: 'canViewRams' },
  { id: 'toolbox-talks', label: 'Toolbox Talks', icon: 'MessageSquare', visibilityKey: 'canViewToolboxTalks' },
  { id: 'inductions', label: 'Inductions', icon: 'UserCheck', visibilityKey: 'canViewInductions' },
  { id: 'compliance', label: 'Compliance', icon: 'BadgeCheck', visibilityKey: 'canViewCompliance' },
];

// Legacy tabs that use the old per-share permissions (canViewTimeline etc.)
const LEGACY_TABS = [
  { id: 'timeline', label: 'Activity Timeline', icon: 'Activity', permKey: 'canViewTimeline' },
  { id: 'photos', label: 'Site Photos', icon: 'Images', permKey: 'canViewPhotos' },
  { id: 'documents', label: 'Documents', icon: 'FileText', permKey: 'canViewDocuments' },
  { id: 'compliance', label: 'Compliance Reports', icon: 'ShieldCheck', permKey: 'canViewReports' },
  { id: 'notes', label: 'Notes', icon: 'StickyNote', permKey: null },
];

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Track live visibility per selected site (refreshed on site change)
  const [liveVisibility, setLiveVisibility] = useState(null);
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  useEffect(() => {
    loadSharedSites();
  }, []);

  const loadSharedSites = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await clientService?.getSharedSites();
      if (err) throw err;
      setSites(data || []);
      if (data?.length > 0) {
        setSelectedSite(data?.[0]);
      }
    } catch (err) {
      console.error('[ClientDashboard] load sites failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading your sites: ${err?.message}`);
      } else {
        setError(`Failed to load your sites: ${err?.message || 'Unknown error'}. Please refresh the page.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch fresh visibility from DB whenever selected site changes.
  // This ensures permissions are always current after logout/login or admin toggle changes.
  useEffect(() => {
    if (!selectedSite?.id) {
      setLiveVisibility(null);
      return;
    }
    let cancelled = false;
    const fetchVisibility = async () => {
      setVisibilityLoading(true);
      const { data, error: err } = await clientService?.getSiteVisibility(selectedSite?.id);
      if (cancelled) return;
      if (err && err?.code !== 'NO_SHARE') {
        // Real failure (RLS/network/etc) — log but fall back to cached visibility
        // rather than blocking the UI. NO_SHARE just means the row is missing,
        // which is a legitimate "use defaults" state, not an error.
        console.error('[ClientDashboard] fetch visibility failed:', { code: err?.code, message: err?.message });
      }
      setLiveVisibility(data || selectedSite?.siteVisibility || null);
      setVisibilityLoading(false);
    };
    fetchVisibility();
    return () => { cancelled = true; };
  }, [selectedSite?.id]);

  const handleSiteSelect = useCallback((site) => {
    setSelectedSite(site);
    setActiveTab('timeline');
    setLiveVisibility(null);
  }, []);

  // Derive visible tabs based on LIVE site-level visibility settings (server-authoritative)
  const visibleTabs = useMemo(() => {
    if (!selectedSite) return LEGACY_TABS;
    // Prefer liveVisibility (freshly fetched from DB) over cached siteVisibility
    const sv = liveVisibility || selectedSite?.siteVisibility;
    const perms = selectedSite?.permissions;

    // If siteVisibility is present (new columns exist), use the full 13-module system
    if (sv) {
      return ALL_TABS?.filter(tab => {
        if (tab?.visibilityKey && sv?.[tab?.visibilityKey] === false) return false;
        return true;
      });
    }

    // Fallback: legacy 4-tab system using per-share permissions
    return LEGACY_TABS?.filter(tab => {
      if (tab?.id === 'timeline') return perms?.canViewTimeline !== false;
      if (tab?.id === 'photos') return perms?.canViewPhotos !== false;
      if (tab?.id === 'documents') return perms?.canViewDocuments !== false;
      if (tab?.id === 'compliance') return perms?.canViewReports !== false;
      return true;
    });
  }, [selectedSite, liveVisibility]);

  // Guard: if activeTab is not in visibleTabs, redirect to first visible
  useEffect(() => {
    if (!selectedSite || visibilityLoading) return;
    const isAllowed = visibleTabs?.some(t => t?.id === activeTab);
    if (!isAllowed && visibleTabs?.length > 0) {
      setActiveTab(visibleTabs?.[0]?.id);
    }
  }, [activeTab, visibleTabs, selectedSite, visibilityLoading]);

  // Helper: check if a specific tab is accessible (used for direct-URL guard)
  const isTabAccessible = useCallback((tabId) => {
    return visibleTabs?.some(t => t?.id === tabId);
  }, [visibleTabs]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Client Portal | SiteProof</title>
      </Helmet>
      <Header />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">

          <SubscriptionReadOnlyBanner className="mb-6" />

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Icon name="Building2" size={20} className="text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground font-heading">Client Portal</h1>
                <p className="text-sm text-muted-foreground">
                  Welcome, {userProfile?.full_name || 'Client'} — view your shared project sites below
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
                <p className="text-sm text-muted-foreground">Loading your sites…</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="AlertCircle" size={28} className="text-red-500" />
              </div>
              <p className="text-foreground font-medium">{error}</p>
              <button
                onClick={loadSharedSites}
                className="px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : sites?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                <Icon name="Building2" size={36} className="text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-foreground mb-1">No Sites Shared Yet</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  No sites have been shared with you yet. Please contact your project manager to request access.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-6">

              {/* Sidebar: Site List */}
              <aside className="lg:w-72 flex-shrink-0">
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-3 border-b border-border bg-muted/30">
                    <h2 className="text-sm font-semibold text-foreground">Your Sites</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{sites?.length} site{sites?.length !== 1 ? 's' : ''} shared with you</p>
                  </div>
                  <div className="divide-y divide-border">
                    {sites?.map((site) => (
                      <button
                        key={site?.id}
                        onClick={() => handleSiteSelect(site)}
                        className={`w-full text-left px-4 py-3.5 hover:bg-muted/50 transition-colors ${
                          selectedSite?.id === site?.id ? 'bg-accent/10 border-l-2 border-accent' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">{site?.site_name}</p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {site?.city}{site?.postcode ? `, ${site?.postcode}` : ''}
                            </p>
                          </div>
                          <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                            STATUS_COLORS?.[site?.status] || 'bg-gray-100 text-gray-600'
                          }`}>
                            {STATUS_LABELS?.[site?.status] || site?.status}
                          </span>
                        </div>
                        {site?.compliance_status && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <Icon
                              name={site?.compliance_status === 'compliant' ? 'ShieldCheck' : site?.compliance_status === 'warning' ? 'ShieldAlert' : 'ShieldX'}
                              size={12}
                              className={COMPLIANCE_COLORS?.[site?.compliance_status] || 'text-muted-foreground'}
                            />
                            <span className={`text-xs capitalize font-medium ${
                              COMPLIANCE_COLORS?.[site?.compliance_status] || 'text-muted-foreground'
                            }`}>
                              {site?.compliance_status}
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {selectedSite ? (
                  <>
                    {/* Site Header */}
                    <div className="bg-card rounded-xl border border-border p-5 mb-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h2 className="text-xl font-bold text-foreground">{selectedSite?.site_name}</h2>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              STATUS_COLORS?.[selectedSite?.status] || 'bg-gray-100 text-gray-600'
                            }`}>
                              {STATUS_LABELS?.[selectedSite?.status] || selectedSite?.status}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {[selectedSite?.address_line1, selectedSite?.city, selectedSite?.postcode]?.filter(Boolean)?.join(', ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 bg-muted/50 px-3 py-1.5 rounded-lg">
                          <Icon name="Lock" size={13} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground font-medium">Read Only</span>
                        </div>
                      </div>
                      {(selectedSite?.start_date || selectedSite?.end_date) && (
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
                          {selectedSite?.start_date && (
                            <div className="flex items-center gap-1.5">
                              <Icon name="CalendarDays" size={13} className="text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                Start: {new Date(selectedSite?.start_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          )}
                          {selectedSite?.end_date && (
                            <div className="flex items-center gap-1.5">
                              <Icon name="CalendarCheck" size={13} className="text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                End: {new Date(selectedSite?.end_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 bg-muted/40 rounded-xl p-1 mb-5 overflow-x-auto scrollbar-hide">
                      {visibilityLoading ? (
                        <div className="flex items-center gap-2 px-4 py-2.5">
                          <Icon name="Loader2" size={14} className="animate-spin text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Loading access…</span>
                        </div>
                      ) : (
                        visibleTabs?.map((tab) => (
                          <button
                            key={tab?.id}
                            onClick={() => setActiveTab(tab?.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all shrink-0 ${
                              activeTab === tab?.id
                                ? 'bg-card text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            <Icon name={tab?.icon} size={16} />
                            {tab?.label}
                          </button>
                        ))
                      )}
                    </div>

                    {/* Tab Content */}
                    {/* Access denied guard: blocks direct URL / tab access when section is disabled */}
                    {selectedSite && !visibilityLoading && !isTabAccessible(activeTab) ? (
                      <div className="bg-card rounded-xl border border-border p-10 flex flex-col items-center justify-center gap-4 text-center">
                        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                          <Icon name="Lock" size={26} className="text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-foreground mb-1">Section Not Available</h3>
                          <p className="text-sm text-muted-foreground max-w-sm">
                            This section has been disabled for your account on this site. Please contact your project manager if you need access.
                          </p>
                        </div>
                        {visibleTabs?.length > 0 && (
                          <button
                            onClick={() => setActiveTab(visibleTabs?.[0]?.id)}
                            className="px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
                          >
                            Go to {visibleTabs?.[0]?.label}
                          </button>
                        )}
                      </div>
                    ) : visibilityLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <Icon name="Loader2" size={24} className="animate-spin text-accent" />
                      </div>
                    ) : (
                      <>
                        {activeTab === 'overview' && (
                          <ClientSiteOverview site={selectedSite} />
                        )}
                        {activeTab === 'timeline' && (
                          <ClientActivityTimeline siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'photos' && (
                          <ClientPhotoGallery siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'documents' && (
                          <ClientDocuments siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'drawings' && (
                          <ClientDrawingsList siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'compliance' && (
                          <ClientComplianceReport siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'snags' && (
                          <ClientSnagsList siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'reports' && (
                          <ClientReportsList siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'site-diary' && (
                          <ClientDailyProgress siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'rams' && (
                          <ClientRamsList siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'toolbox-talks' && (
                          <ClientToolboxTalksList siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'inductions' && (
                          <ClientInductionsList siteId={selectedSite?.id} siteName={selectedSite?.site_name} />
                        )}
                        {activeTab === 'notes' && (
                          <ClientSiteNotes siteId={selectedSite?.id} companyId={selectedSite?.company_id} />
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center py-24">
                    <p className="text-muted-foreground text-sm">Select a site from the list to view details.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ─── Inline read-only module components ──────────────────────────────────────

const ClientModuleWrapper = ({ icon, title, children, loading, empty, emptyMessage }) => (
  <div className="bg-card rounded-xl border border-border overflow-hidden">
    <div className="px-5 py-4 border-b border-border flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
        <Icon name={icon} size={16} className="text-accent" />
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    </div>
    <div className="p-5">
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Icon name="Loader2" size={22} className="animate-spin text-accent" />
        </div>
      ) : empty ? (
        <div className="text-center py-10">
          <Icon name={icon} size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{emptyMessage || 'No records found.'}</p>
        </div>
      ) : children}
    </div>
  </div>
);

const ClientSnagsList = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { supabase: sb } = React.useMemo(() => ({ supabase: null }), []);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { createClient } = await import('../../lib/supabase');
        const { supabase: client } = await import('../../lib/supabase');
        const { data } = await client
          ?.from('snags')
          ?.select('id, title, status, priority, created_at, location')
          ?.eq('site_id', siteId)
          // NSherjFh PR 4: client portal snag list hides soft-deleted snags.
          ?.is('deleted_at', null)
          ?.order('created_at', { ascending: false })
          ?.limit(50);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  const PRIORITY_COLORS = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-blue-100 text-blue-700' };
  const STATUS_COLORS = { open: 'bg-orange-100 text-orange-700', in_progress: 'bg-blue-100 text-blue-700', resolved: 'bg-emerald-100 text-emerald-700', closed: 'bg-gray-100 text-gray-600' };

  return (
    <ClientModuleWrapper icon="ClipboardList" title="Snags" loading={loading} empty={items?.length === 0} emptyMessage="No snags recorded for this site.">
      <div className="space-y-2">
        {items?.map(item => (
          <div key={item?.id} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item?.title}</p>
              {item?.location && <p className="text-xs text-muted-foreground mt-0.5">{item?.location}</p>}
              <p className="text-xs text-muted-foreground mt-0.5">{new Date(item.created_at)?.toLocaleDateString('en-GB')}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS?.[item?.status] || 'bg-gray-100 text-gray-600'}`}>{item?.status?.replace('_', ' ')}</span>
              {item?.priority && <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PRIORITY_COLORS?.[item?.priority] || 'bg-gray-100 text-gray-600'}`}>{item?.priority}</span>}
            </div>
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

const ClientNearMissesList = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { supabase: client } = await import('../../lib/supabase');
        const { data } = await client
          ?.from('near_misses')
          ?.select('id, title, description, severity, incident_date, created_at')
          ?.eq('site_id', siteId)
          ?.order('incident_date', { ascending: false })
          ?.limit(50);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  const SEV_COLORS = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-blue-100 text-blue-700' };

  return (
    <ClientModuleWrapper icon="AlertTriangle" title="Issues / Near Misses" loading={loading} empty={items?.length === 0} emptyMessage="No near misses or issues recorded for this site.">
      <div className="space-y-2">
        {items?.map(item => (
          <div key={item?.id} className="p-3 rounded-lg bg-muted/30 border border-border/60">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium text-foreground">{item?.title}</p>
              {item?.severity && <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${SEV_COLORS?.[item?.severity] || 'bg-gray-100 text-gray-600'}`}>{item?.severity}</span>}
            </div>
            {item?.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item?.description}</p>}
            <p className="text-xs text-muted-foreground mt-1">{new Date(item.incident_date || item.created_at)?.toLocaleDateString('en-GB')}</p>
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

const ClientDailyProgress = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { supabase: client } = await import('../../lib/supabase');
        // NB: pre-existing bug here — weather_conditions / work_summary /
        // workers_on_site are NOT columns on daily_site_records (actual
        // columns: weather, work_completed, no workers_on_site). PostgREST
        // errors and the try/catch swallows it, list silently renders
        // empty. Out of scope for NSherjFh PR 6; cascade-rule filter
        // added so the deleted_at gate is already in place when the
        // column refs are fixed. Mirrors the pre-existing bug in PR 3's
        // ClientRamsList query.
        const { data } = await client
          ?.from('daily_site_records')
          ?.select('id, record_date, weather_conditions, work_summary, workers_on_site, created_at')
          ?.eq('site_id', siteId)
          ?.is('deleted_at', null)
          ?.order('record_date', { ascending: false })
          ?.limit(30);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  return (
    <ClientModuleWrapper icon="CalendarCheck" title="Daily Progress" loading={loading} empty={items?.length === 0} emptyMessage="No daily progress records for this site.">
      <div className="space-y-3">
        {items?.map(item => (
          <div key={item?.id} className="p-4 rounded-lg bg-muted/30 border border-border/60">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-sm font-semibold text-foreground">{new Date(item.record_date)?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
              {item?.workers_on_site != null && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{item?.workers_on_site} workers</span>
              )}
            </div>
            {item?.weather_conditions && <p className="text-xs text-muted-foreground mb-1">Weather: {item?.weather_conditions}</p>}
            {item?.work_summary && <p className="text-sm text-foreground/80 text-sm leading-relaxed">{item?.work_summary}</p>}
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

const ClientRamsList = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { supabase: client } = await import('../../lib/supabase');
        // NB: pre-existing bug here — `description`, `status`, `valid_from`,
        // `valid_until` are NOT columns on rams_documents. PostgREST errors,
        // the try/catch below swallows it, and the list silently renders
        // empty. Out of scope for NSherjFh PR 3; cascade-rule filter added
        // for completeness so when the column refs are fixed, soft-deleted
        // H&S docs already disappear from the client portal.
        const { data } = await client
          ?.from('rams_documents')
          ?.select('id, title, description, status, created_at, valid_from, valid_until')
          ?.eq('site_id', siteId)
          ?.is('deleted_at', null)
          ?.order('created_at', { ascending: false })
          ?.limit(30);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  const STATUS_COLORS = { active: 'bg-emerald-100 text-emerald-700', draft: 'bg-amber-100 text-amber-700', expired: 'bg-red-100 text-red-700', archived: 'bg-gray-100 text-gray-600' };

  return (
    <ClientModuleWrapper icon="ShieldCheck" title="H&S Docs" loading={loading} empty={items?.length === 0} emptyMessage="No H&S documents for this site.">
      <div className="space-y-2">
        {items?.map(item => (
          <div key={item?.id} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item?.title}</p>
              {item?.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item?.description}</p>}
              <p className="text-xs text-muted-foreground mt-0.5">{new Date(item.created_at)?.toLocaleDateString('en-GB')}</p>
            </div>
            {item?.status && <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${STATUS_COLORS?.[item?.status] || 'bg-gray-100 text-gray-600'}`}>{item?.status}</span>}
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

const ClientToolboxTalksList = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { supabase: client } = await import('../../lib/supabase');
        // NB: pre-existing bug here — `topic`, `scheduled_date`,
        // `status` are NOT columns on toolbox_talks (actual columns:
        // title, file_path only). PostgREST errors, the try/catch
        // below swallows it, list silently renders empty. Out of
        // scope for NSherjFh PR 10; cascade-rule filter added for
        // completeness so when the column refs are fixed, soft-
        // deleted talks already disappear from the client portal.
        // Same shape as PR 3 ClientRamsList, PR 6 ClientDailyProgress
        // List, PR 9 ClientInductionsList.
        const { data } = await client
          ?.from('toolbox_talks')
          ?.select('id, title, topic, scheduled_date, status, created_at')
          ?.eq('site_id', siteId)
          ?.is('deleted_at', null)
          ?.order('scheduled_date', { ascending: false })
          ?.limit(30);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  const STATUS_COLORS = { completed: 'bg-emerald-100 text-emerald-700', scheduled: 'bg-blue-100 text-blue-700', cancelled: 'bg-red-100 text-red-700' };

  return (
    <ClientModuleWrapper icon="MessageSquare" title="Toolbox Talks" loading={loading} empty={items?.length === 0} emptyMessage="No toolbox talks recorded for this site.">
      <div className="space-y-2">
        {items?.map(item => (
          <div key={item?.id} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item?.title}</p>
              {item?.topic && <p className="text-xs text-muted-foreground mt-0.5 truncate">{item?.topic}</p>}
              <p className="text-xs text-muted-foreground mt-0.5">{new Date(item.scheduled_date || item.created_at)?.toLocaleDateString('en-GB')}</p>
            </div>
            {item?.status && <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${STATUS_COLORS?.[item?.status] || 'bg-gray-100 text-gray-600'}`}>{item?.status}</span>}
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

// Client-facing notes — shows only shared notes (read-only for clients)
const ClientSiteNotes = ({ siteId, companyId }) => {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Site Notes</h3>
        <p className="text-sm text-muted-foreground">Shared updates and communications from your project team.</p>
      </div>
      {companyId ? (
        <NotesFeed
          entityType="site"
          entityId={siteId}
          companyId={companyId}
          title="Shared Notes"
        />
      ) : (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <Icon name="MessageSquare" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Notes unavailable</p>
        </div>
      )}
    </div>
  );
};

// ─── New inline module components ────────────────────────────────────────────

const ClientSiteOverview = ({ site }) => {
  if (!site) return null;
  const STATUS_COLORS = {
    active: 'bg-emerald-100 text-emerald-700',
    planning: 'bg-blue-100 text-blue-700',
    on_hold: 'bg-amber-100 text-amber-700',
    completed: 'bg-slate-100 text-slate-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  const STATUS_LABELS = {
    active: 'Active', planning: 'Planning', on_hold: 'On Hold',
    completed: 'Completed', archived: 'Archived',
  };
  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="LayoutDashboard" size={15} className="text-accent" />
          Site Overview
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Site Name</p>
            <p className="text-sm font-medium text-foreground">{site?.site_name || '—'}</p>
          </div>
          {site?.site_reference && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Reference</p>
              <p className="text-sm font-medium text-foreground">{site?.site_reference}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Status</p>
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS?.[site?.status] || 'bg-gray-100 text-gray-600'}`}>
              {STATUS_LABELS?.[site?.status] || site?.status || '—'}
            </span>
          </div>
          {site?.city && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Location</p>
              <p className="text-sm font-medium text-foreground">
                {[site?.address_line1, site?.city, site?.postcode]?.filter(Boolean)?.join(', ')}
              </p>
            </div>
          )}
          {site?.start_date && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Start Date</p>
              <p className="text-sm font-medium text-foreground">
                {new Date(site?.start_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          )}
          {site?.end_date && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Expected Completion</p>
              <p className="text-sm font-medium text-foreground">
                {new Date(site?.end_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClientDrawingsList = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { supabase: client } = await import('../../lib/supabase');
        const { data } = await client
          ?.from('drawings')
          ?.select('id, title, discipline, revision, status, created_at, drawing_number')
          ?.eq('site_id', siteId)
          ?.order('created_at', { ascending: false })
          ?.limit(50);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  const STATUS_COLORS = {
    approved: 'bg-emerald-100 text-emerald-700',
    for_review: 'bg-amber-100 text-amber-700',
    superseded: 'bg-gray-100 text-gray-600',
    draft: 'bg-blue-100 text-blue-700',
  };

  return (
    <ClientModuleWrapper icon="FileImage" title="Drawings" loading={loading} empty={items?.length === 0} emptyMessage="No drawings available for this site.">
      <div className="space-y-2">
        {items?.map(item => (
          <div key={item?.id} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item?.title}</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {item?.drawing_number && <span className="text-xs text-muted-foreground">#{item?.drawing_number}</span>}
                {item?.discipline && <span className="text-xs text-muted-foreground">{item?.discipline}</span>}
                {item?.revision && <span className="text-xs text-muted-foreground">Rev {item?.revision}</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{new Date(item?.created_at)?.toLocaleDateString('en-GB')}</p>
            </div>
            {item?.status && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${STATUS_COLORS?.[item?.status] || 'bg-gray-100 text-gray-600'}`}>
                {item?.status?.replace('_', ' ')}
              </span>
            )}
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

const ClientReportsList = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { supabase: client } = await import('../../lib/supabase');
        const { data } = await client
          ?.from('site_reports')
          ?.select('id, title, report_type, status, created_at, report_date')
          ?.eq('site_id', siteId)
          ?.order('created_at', { ascending: false })
          ?.limit(30);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  return (
    <ClientModuleWrapper icon="BarChart2" title="Reports" loading={loading} empty={items?.length === 0} emptyMessage="No reports available for this site.">
      <div className="space-y-2">
        {items?.map(item => (
          <div key={item?.id} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item?.title}</p>
              {item?.report_type && <p className="text-xs text-muted-foreground mt-0.5 capitalize">{item?.report_type?.replace('_', ' ')}</p>}
              <p className="text-xs text-muted-foreground mt-0.5">
                {new Date(item?.report_date || item?.created_at)?.toLocaleDateString('en-GB')}
              </p>
            </div>
            {item?.status && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 bg-blue-100 text-blue-700">
                {item?.status}
              </span>
            )}
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

const ClientInductionsList = ({ siteId }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { supabase: client } = await import('../../lib/supabase');
        // NB: pre-existing bug here — `description`, `status`, `valid_
        // until` are NOT columns on site_inductions (actual columns:
        // title, file_path only). PostgREST errors, the try/catch
        // below swallows it, list silently renders empty. Out of
        // scope for NSherjFh PR 9; cascade-rule filter added for
        // completeness so when the column refs are fixed, soft-
        // deleted inductions already disappear from the client
        // portal. Same shape as PR 3 ClientRamsList + PR 6 Client
        // DailyProgress.
        const { data } = await client
          ?.from('site_inductions')
          ?.select('id, title, description, status, created_at, valid_until')
          ?.eq('site_id', siteId)
          ?.is('deleted_at', null)
          ?.order('created_at', { ascending: false })
          ?.limit(30);
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    };
    if (siteId) load();
  }, [siteId]);

  const STATUS_COLORS = {
    active: 'bg-emerald-100 text-emerald-700',
    draft: 'bg-amber-100 text-amber-700',
    archived: 'bg-gray-100 text-gray-600',
  };

  return (
    <ClientModuleWrapper icon="UserCheck" title="Inductions" loading={loading} empty={items?.length === 0} emptyMessage="No inductions recorded for this site.">
      <div className="space-y-2">
        {items?.map(item => (
          <div key={item?.id} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item?.title}</p>
              {item?.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item?.description}</p>}
              <p className="text-xs text-muted-foreground mt-0.5">{new Date(item?.created_at)?.toLocaleDateString('en-GB')}</p>
            </div>
            {item?.status && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${STATUS_COLORS?.[item?.status] || 'bg-gray-100 text-gray-600'}`}>
                {item?.status}
              </span>
            )}
          </div>
        ))}
      </div>
    </ClientModuleWrapper>
  );
};

export default ClientDashboard;
